/**
 * The organisation-level settings for payroll's "apply merit to payroll" capability (remediation
 * plan §27/§28, decisions D2/P1). Declared on its own, out of `payroll-merit.contract.ts` on purpose:
 * that file is the two-reviewer payroll surface (Siddhant Yerandkar / Chirag Gehlot), and settings
 * CRUD is unrelated churn they shouldn't have to review.
 *
 * WHO. `compensation:update` at ORGANISATION scope — in practice the Global Admin (`org_owner`), or
 * anyone a tenant has explicitly granted that permission org-wide. An ordinary, legal-entity-scoped
 * payroll admin cannot flip this: a target with no `legalEntityId` is only satisfiable by an
 * org-scoped grant (`can.ts`'s `scopeCovers`) — verified, not assumed (plan §27).
 *
 * THE MODULE SWITCH (§27). `MeritPayrollSettings.enabled` defaults to OFF for every organisation,
 * old and new — nothing changes until an admin opts in. While off, `payroll-merit.ts`'s two routes
 * (`listPendingMeritRevisions`, `applyMeritProposal`) answer 404 `merit_apply_disabled`; the merit
 * recommendation/approval workflow itself (`merit.contract.ts`) is untouched, so approved proposals
 * simply wait, unconsumed, until the switch is turned on. THIS router is the exception:
 * `getModuleStatus`, `getSettings` and `updateSettings` answer while the module is off, or it could
 * never be switched on — the same shape as Travel Desk's own settings router (ADR 0030 D33).
 * `addCompensation`'s overlap check and the `compensations_no_overlap` DB trigger are NOT gated by
 * this switch and have no setting here — both stay always-on (plan §27).
 *
 * NET-BASIS ARREARS MODE (§28, decision P1). `netBasisArrearsMode` governs a back-dated increase's
 * arrears when the employment is on a `net` (take-home) pay basis: `accept_shortfall` (default) or
 * `block`. See `merit-payroll-settings.ts` for what each means and why `gross_up` is named but not
 * offered here yet.
 *
 * Configuration writes take no Idempotency-Key (not a financial mutation, invariant 10) but the row
 * carries `version`, and a stale write is a 409 `stale_version` — the same pattern every other
 * settings router in this codebase uses (travel, payroll company/statutory, …).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, instantSchema } from '../schemas/common.js';
import { netBasisArrearsModeSchema } from '../schemas/merit-payroll-settings.js';
import { routeMeta } from './meta.js';

const c = initContract();

const settingsVersionSchema = z.number().int().nonnegative();

/** One row per organisation. */
export const meritPayrollSettingsSchema = z.object({
  /** §27. Off: the two payroll routes answer 404 `merit_apply_disabled`. */
  enabled: z.boolean(),
  /** §28 P1. `accept_shortfall` (default) or `block`. */
  netBasisArrearsMode: netBasisArrearsModeSchema,
  version: settingsVersionSchema,
  updatedAt: instantSchema,
});
export type MeritPayrollSettings = z.infer<typeof meritPayrollSettingsSchema>;

/** What any signed-in user may ask (`getModuleStatus`): is the switch on for my organisation. */
export const meritPayrollModuleStatusSchema = z.object({
  enabled: z.boolean(),
});
export type MeritPayrollModuleStatus = z.infer<typeof meritPayrollModuleStatusSchema>;

export const updateMeritPayrollSettingsBodySchema = z.object({
  version: settingsVersionSchema,
  enabled: z.boolean().optional(),
  netBasisArrearsMode: netBasisArrearsModeSchema.optional(),
});
export type UpdateMeritPayrollSettingsBody = z.infer<typeof updateMeritPayrollSettingsBodySchema>;

const writeSettingsMeta = routeMeta('compensation:update');

export const payrollMeritSettingsContract = c.router(
  {
    getModuleStatus: {
      method: 'GET',
      path: '/status',
      responses: {
        200: meritPayrollModuleStatusSchema,
        401: errorBodySchema,
      },
      summary: 'Is "apply merit to payroll" switched on for this organisation',
      metadata: routeMeta(null),
    },

    getSettings: {
      method: 'GET',
      path: '/settings',
      responses: {
        200: meritPayrollSettingsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Merit Auto-Apply settings: the module switch and the net-basis-arrears mode',
      metadata: writeSettingsMeta,
    },

    /** Exempt from the module switch, or it could never be switched on. */
    updateSettings: {
      method: 'PATCH',
      path: '/settings',
      body: updateMeritPayrollSettingsBodySchema,
      responses: {
        200: meritPayrollSettingsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Stale `version`. */
        409: errorBodySchema,
      },
      summary: 'Switch "apply merit to payroll" on or off, and set the net-basis-arrears mode',
      metadata: writeSettingsMeta,
    },
  },
  { pathPrefix: '/payroll/merit-settings' },
);
